import { useEffect, useRef } from "react";

// Foil overlay for legendary cards. Direct port of the reverse-holo
// technique from simeydotme/pokemon-cards-css (public/css/cards/
// reverse-holo.css + base.css). Two layers:
//   - shine: three stacked backgrounds (radial / -45deg gradient /
//     procedural noise) blended soft-light → difference, composited
//     onto the card with color-dodge.
//   - glare: two compounded radial spotlights with mix-blend overlay.
// Both are gated by --card-opacity so there's no idle effect.
//
// The shine's opacity formula `(1.5 * card-opacity) - pointer-from-
// center` is from pokemon-cards-css: brightest near the cursor, fades
// at the corners. The glare's dark-edge stop (NOT transparent) matters:
// with overlay blend, the dark edge suppresses overall brightness.
//
// The Card sets `isolation: isolate` so mix-blend layers blend with
// the card's interior content (art, text, rarity wash) rather than
// whatever sits outside the card.

interface CardFoilProps {
  rarity: string | null | undefined;
}

const LEGENDARY_FOIL_ENABLED = false;

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export function CardFoil({ rarity }: CardFoilProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    const parent = node?.parentElement;
    if (!node || !parent) return;
    const onMove = (e: MouseEvent) => {
      const r = parent.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) return;
      const px = clamp01((e.clientX - r.left) / r.width);
      const py = clamp01((e.clientY - r.top) / r.height);
      const fromCenter = clamp01(
        Math.sqrt((px - 0.5) * (px - 0.5) + (py - 0.5) * (py - 0.5)) / 0.5,
      );
      // Mirror writes to both parent (Card) and our own node — tests
      // inspect the foil node directly.
      const set = (el: HTMLElement) => {
        el.style.setProperty("--pointer-x", `${(px * 100).toFixed(2)}%`);
        el.style.setProperty("--pointer-y", `${(py * 100).toFixed(2)}%`);
        el.style.setProperty("--pointer-from-left", px.toFixed(3));
        el.style.setProperty("--pointer-from-top", py.toFixed(3));
        el.style.setProperty("--pointer-from-center", fromCenter.toFixed(3));
        el.style.setProperty("--card-opacity", "1");
      };
      set(parent);
      set(node);
    };
    const onLeave = () => {
      parent.style.setProperty("--card-opacity", "0");
      node.style.setProperty("--card-opacity", "0");
    };
    parent.addEventListener("mousemove", onMove);
    parent.addEventListener("mouseleave", onLeave);
    return () => {
      parent.removeEventListener("mousemove", onMove);
      parent.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  if (!LEGENDARY_FOIL_ENABLED || rarity !== "legendary") return null;

  return (
    <div
      ref={ref}
      data-testid="card-foil"
      className="card-foil"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        // No zIndex — would create an inner stacking context and
        // trap the mix-blend layers with no backdrop.
      }}
    >
      <div className="card-foil__shine" />
      <div className="card-foil__glare card-foil__glare--a" />
      <div className="card-foil__glare card-foil__glare--b" />
    </div>
  );
}

// Procedural grayscale noise for the shine's third layer. The
// difference blend collapses without high-frequency luminance
// variation. The colorMatrix rows 1-3 (`1 0 0 0 0`) collapse R/G/B
// inputs into a single channel (R), giving grayscale; row 4
// (`0 0 0 0 1`) forces alpha to opaque.
const FOIL_NOISE_URL =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 240'><filter id='n' x='0' y='0'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' seed='4' stitchTiles='stitch'/><feColorMatrix values='1 0 0 0 0  1 0 0 0 0  1 0 0 0 0  0 0 0 0 1'/></filter><rect width='240' height='240' filter='url(%23n)'/></svg>\")";

const FOIL_CSS = `
.card-foil {
  --pointer-x: 50%;
  --pointer-y: 50%;
  --pointer-from-left: 0.5;
  --pointer-from-top: 0.5;
  --pointer-from-center: 0;
  --card-opacity: 0;
  transition:
    --card-opacity 220ms ease-out,
    --pointer-x 80ms linear,
    --pointer-y 80ms linear,
    --pointer-from-left 80ms linear,
    --pointer-from-top 80ms linear,
    --pointer-from-center 80ms linear;
}
.card-foil__shine,
.card-foil__glare {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 4;
}
.card-foil__shine {
  background-image:
    radial-gradient(
      circle at var(--pointer-x) var(--pointer-y),
      hsl(0, 0%, 100%) 5%,
      hsl(0, 0%, 0%)   50%,
      hsl(0, 0%, 100%) 80%
    ),
    linear-gradient(
      -45deg,
      hsl(0, 0%, 0%)   15%,
      hsl(0, 0%, 100%) 50%,
      hsl(0, 0%, 0%)   85%
    ),
    ${FOIL_NOISE_URL};
  background-size:
    120% 120%,
    200% 200%,
    cover;
  background-position:
    center center,
    calc(100% * var(--pointer-from-left)) calc(100% * var(--pointer-from-top)),
    center center;
  background-blend-mode: soft-light, difference;
  filter: brightness(0.55) contrast(1.5) saturate(1);
  mix-blend-mode: color-dodge;
  opacity: calc((1.5 * var(--card-opacity)) - var(--pointer-from-center));
}
.card-foil__glare--a {
  background-image: radial-gradient(
    farthest-corner circle at var(--pointer-x) var(--pointer-y),
    hsla(0, 0%, 100%, 0.8) 10%,
    hsla(0, 0%, 100%, 0.5) 20%,
    hsla(0, 0%, 0%, 0.75) 90%
  );
  filter: brightness(0.7) contrast(1.5);
  mix-blend-mode: overlay;
  opacity: var(--card-opacity);
}
.card-foil__glare--b {
  background-image: radial-gradient(
    farthest-corner circle at var(--pointer-x) var(--pointer-y),
    hsl(0, 0%, 100%) 10%,
    hsla(0, 0%, 100%, 0.5) 20%,
    hsla(0, 0%, 0%, 0.5) 120%
  );
  filter: brightness(1) contrast(1.5);
  mix-blend-mode: overlay;
  opacity: var(--card-opacity);
}
/* inherits: true required so values set on the wrapper propagate
   down to the gradient layers. */
@property --pointer-x         { syntax: "<percentage>"; inherits: true; initial-value: 50%; }
@property --pointer-y         { syntax: "<percentage>"; inherits: true; initial-value: 50%; }
@property --pointer-from-left   { syntax: "<number>"; inherits: true; initial-value: 0.5; }
@property --pointer-from-top    { syntax: "<number>"; inherits: true; initial-value: 0.5; }
@property --pointer-from-center { syntax: "<number>"; inherits: true; initial-value: 0; }
@property --card-opacity        { syntax: "<number>"; inherits: true; initial-value: 0; }
`;

if (typeof document !== "undefined" && !document.getElementById("card-foil-styles")) {
  const el = document.createElement("style");
  el.id = "card-foil-styles";
  el.textContent = FOIL_CSS;
  document.head.appendChild(el);
}
